import { smoothScrollIntoView } from '@domgen/base-design-system-2.0/animation/smoothScrollIntoView';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import type { AddressSearchResult } from '@domgen/shared/models/services/api/getAddresses';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { buildAddressSubmissionFields } from '@domgen/shared/utils/address/buildAddressSubmission';
import { hasPoBoxAddress } from '@domgen/shared/utils/address/hasPoBoxAddress';
import {
  restoreMailingSelectedAddress,
  restoreMerchandiseSelectedAddress,
} from '@domgen/shared/utils/address/restoreSelectedAddress';
import {
  extractMailingAddress,
  extractMerchandiseAddress,
  validateAddressMatch,
} from '@domgen/shared/utils/address/validateAddressMatch';
import { usePostHog } from '@posthog/react';
import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from '../../assets/icons/ArrowLeft';
import { PageBackgroundWrapper } from '../../components/PageBackgroundWrapper';
import { FormKey } from '../../enums/formKey';
import { StepKey } from '../../enums/stepKey';
import { StoreEvent } from '../../enums/storeEvent';
import { FormChangeEvent, useFormValidation } from '../../hooks/useFormValidation';
import { QuoteStatus } from '../../models/store/quoteStatus';
import { capturePostHogEvent } from '../../services/posthog/capture';
import { useApplianceFormData } from '../../store/selectors/useApplianceFormData';
import { useAppState } from '../../store/selectors/useAppState';
import { useQuote } from '../../store/selectors/useQuote';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { store } from '../../store/store';
import { handleGaPersonalDetailsFormEvents } from '../../utils/ga/handleGaPersonalDetailsFormEvents';
import { getSystemCode } from '../../utils/getSystemCode';
import { formApplianceName } from '../../utils/store/formApplianceName';
import { formCategoryName } from '../../utils/store/formCategoryName';
import { LoadingPage } from '../LoadingPage';
import { AddressForm } from './components/AddressForm';
import { PersonalDetailsForm } from './components/PersonalDetailsForm';
import { formFieldValidators } from './constants/formFieldValidators';

const styles = {
  pageContainer: 'flex justify-center items-center bg-bg-main py-space-700 px-space-500',
  panel:
    'min-h-100 w-2xl h-auto p-space-500 flex flex-col justify-center rounded-radius-100 bg-bg-main-container',
  errorMessage: 'text-sm text-red-600 mt-1',
  navigation: 'flex flex-col w-full gap-space-500 md:flex-row-reverse md:justify-between',
  formGrid: 'grid grid-cols-8 gap-x-space-400 gap-y-space-500 ',
};

type PersonalDetailsPageProps = {
  stepNumber: number;
  totalSteps: number;
};

export function PersonalDetailsPage({ stepNumber, totalSteps }: PersonalDetailsPageProps) {
  const { VITE_CLIENT_FRIENDLY_NAME } = import.meta.env;
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const quote = useQuote();

  const [merchandiseLocatedAtSameAddress, setMerchandiseLocatedAtSameAddress] = useState(() =>
    validateAddressMatch(
      extractMailingAddress((formData[StepKey.PERSONAL_DETAILS] ?? {}) as Record<string, string>),
      extractMerchandiseAddress(
        (formData[StepKey.PERSONAL_DETAILS] ?? {}) as Record<string, string>,
      ),
    ),
  );
  const [displayMerchandiseLocationAddressForm, setDisplayMerchandiseLocationAddressForm] =
    useState(false);
  const [selectedMerchandiseAddress, setSelectedMerchandiseAddress] =
    useState<AddressSearchResult | null>(() =>
      restoreMerchandiseSelectedAddress(formData[StepKey.PERSONAL_DETAILS]),
    );
  const [selectedSearchAddress, setSelectedSearchAddress] = useState<AddressSearchResult | null>(
    () => restoreMailingSelectedAddress(formData[StepKey.PERSONAL_DETAILS]),
  );
  const posthog = usePostHog();
  const applianceName = formApplianceName.get() ?? '';
  const applianceCategory = formCategoryName.get() ?? '';
  const title =
    !merchandiseLocatedAtSameAddress && displayMerchandiseLocationAddressForm
      ? 'Please tell us the address where the appliance is installed'
      : 'Please enter your details';

  const sessionId = session?.id ?? '';

  useEffect(() => {
    gaPageView({
      pagename: 'personal-details',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: sessionId,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
    });
  }, []);
  const { formValues, errors, handleChange, validateAllFields, setFormValues, setErrorsState } =
    useFormValidation(
      {
        firstName: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.FIRST_NAME] ?? '',
        lastName: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.LAST_NAME] ?? '',
        email: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.EMAIL] ?? '',
        phoneNumber: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.PHONE_NUMBER] ?? '',
        addressLine1: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.ADDRESS_LINE_1] ?? '',
        subBuilding: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.SUB_BUILDING] ?? '',
        city: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.CITY] ?? '',
        regionOrStateCode: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.STATE_CODE] ?? '',
        postalCode: formData[StepKey.PERSONAL_DETAILS]?.[FormKey.POSTAL_CODE] ?? '',
        merchandiseLocationLine1:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_ADDRESS_LINE_1] ?? '',
        merchandiseLocationSubBuilding:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_SUB_BUILDING] ?? '',
        merchandiseLocationCity:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_CITY] ?? '',
        merchandiseLocationRegionOrStateCode:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_STATE_CODE] ?? '',
        merchandiseLocationPostalCode:
          formData[StepKey.PERSONAL_DETAILS]?.[FormKey.MERCHANDISE_POSTAL_CODE] ?? '',
      },
      formFieldValidators,
    );

  const progressBarRef = useRef<HTMLDivElement | null>(null);

  const handleAddressSelect = useCallback(
    (address: AddressSearchResult, addressType?: 'mailing' | 'merchandise') => {
      if (addressType === 'mailing') {
        setSelectedSearchAddress(address);
        setFormValues((prev) => ({
          ...prev,
          [FormKey.ADDRESS_LINE_1]: address.addressLine1,
          [FormKey.SUB_BUILDING]:
            typeof address.subBuilding === 'string' ? address.subBuilding : '',
          [FormKey.CITY]: address.city,
          [FormKey.STATE_CODE]: address.regionOrStateCode,
          [FormKey.POSTAL_CODE]: address.postalCode,
        }));

        Object.entries(address).forEach(([key, value]) => {
          if (value) {
            handleGaPersonalDetailsFormEvents(
              key,
              'personal-details',
              applianceCategory,
              applianceName,
              sessionId,
              systemCode,
              'Auto',
            );
          }
        });
      }
      if (addressType === 'merchandise') {
        setSelectedMerchandiseAddress(address);
        setFormValues((prev) => ({
          ...prev,
          [FormKey.MERCHANDISE_ADDRESS_LINE_1]: address.addressLine1,
          [FormKey.MERCHANDISE_SUB_BUILDING]:
            typeof address.subBuilding === 'string' ? address.subBuilding : '',
          [FormKey.MERCHANDISE_CITY]: address.city,
          [FormKey.MERCHANDISE_STATE_CODE]: address.regionOrStateCode,
          [FormKey.MERCHANDISE_POSTAL_CODE]: address.postalCode,
        }));

        Object.entries(address).forEach(([key, value]) => {
          if (value) {
            handleGaPersonalDetailsFormEvents(
              key,
              'appliance-location',
              applianceCategory,
              applianceName,
              sessionId,
              systemCode,
              'Auto',
            );
          }
        });
      }
    },

    [setSelectedMerchandiseAddress, setSelectedSearchAddress, setFormValues],
  );

  const isMailingAddressPoBox = useMemo(() => {
    const matchesSelectedAddress = validateAddressMatch(
      extractMailingAddress(formValues),
      selectedSearchAddress,
    );
    return hasPoBoxAddress(
      formValues.addressLine1,
      matchesSelectedAddress ? selectedSearchAddress?.poBox : undefined,
    );
  }, [formValues, selectedSearchAddress]);

  useEffect(() => {
    if (isMailingAddressPoBox) {
      setMerchandiseLocatedAtSameAddress(false);
    }
  }, [isMailingAddressPoBox]);

  const handleBack: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      appSessionID: session?.id,
      pagename: displayMerchandiseLocationAddressForm ? 'appliance-location' : 'personal-details',
      regSystemCode: systemCode,
    });

    if (displayMerchandiseLocationAddressForm) {
      setDisplayMerchandiseLocationAddressForm(false);
      capturePostHogEvent(posthog, 'appliance-location-back-clicked', store.get());
    } else {
      store.send({ type: StoreEvent.BACK, data: { stepKey: StepKey.PERSONAL_DETAILS } });
      capturePostHogEvent(posthog, 'personal-details-back-clicked', store.get());
    }
  };

  const validatePersonalDetails = () => {
    const personalDetailsFields = [
      'firstName',
      'lastName',
      'addressLine1',
      'city',
      'regionOrStateCode',
      'postalCode',
      'email',
      'phoneNumber',
    ];
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    personalDetailsFields.forEach((field: string) => {
      const value = formValues[field as keyof typeof formValues];

      const validator = formFieldValidators[field as keyof typeof formFieldValidators]?.find(
        (validation) => typeof validation === 'function',
      ) as (value: string) => string | undefined;

      if (typeof validator === 'function') {
        const error = validator(value);
        if (error) {
          newErrors[field] = error;
          hasErrors = true;
        }
      }
    });
    //Set errors in useFormValidation state
    setErrorsState(newErrors);
    return hasErrors;
  };

  const handleContinue: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const hasErrors = validatePersonalDetails();

    gaEvent({
      pagename: displayMerchandiseLocationAddressForm ? 'appliance-location' : 'personal-details',
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: hasErrors ? 'failed' : 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      appSessionID: session?.id,
    });

    // If merchandise is NOT at same address AND we haven't shown the form yet, show it
    if (!merchandiseLocatedAtSameAddress && !displayMerchandiseLocationAddressForm) {
      if (hasErrors) {
        capturePostHogEvent(posthog, 'personal-details-validation-failed-on-continue', store.get());

        return;
      }

      setDisplayMerchandiseLocationAddressForm(true);
      gaPageView({
        pagename: 'appliance-location',
        category: 'registration-pages',
        type: 'STANDARD',
        appSessionID: session?.id,
        regSystemCode: systemCode,
        applianceName,
        applianceCategory,
      });
      capturePostHogEvent(posthog, 'personal-details-continue-cta-clicked', store.get());
      return;
    }

    // Create updated form values if merchandise is at same address
    let currentFormValues = formValues;

    if (merchandiseLocatedAtSameAddress) {
      currentFormValues = {
        ...formValues,
        [FormKey.MERCHANDISE_ADDRESS_LINE_1]: formValues.addressLine1,
        [FormKey.MERCHANDISE_SUB_BUILDING]: formValues.subBuilding,
        [FormKey.MERCHANDISE_CITY]: formValues.city,
        [FormKey.MERCHANDISE_STATE_CODE]: formValues.regionOrStateCode,
        [FormKey.MERCHANDISE_POSTAL_CODE]: formValues.postalCode,
      };
      setFormValues(currentFormValues);
    }

    const isValid = validateAllFields(currentFormValues);
    if (!isValid) {
      capturePostHogEvent(
        posthog,
        displayMerchandiseLocationAddressForm
          ? 'appliance-location-validation-failed-on-continue'
          : 'personal-details-validation-failed-on-continue',
        store.get(),
      );
      return;
    }

    capturePostHogEvent(
      posthog,
      `${!merchandiseLocatedAtSameAddress && displayMerchandiseLocationAddressForm ? 'appliance-location' : 'personal-details'}-continue-cta-clicked`,
      store.get(),
    );

    onSubmit(currentFormValues);
  };

  const onSubmit = (valuesToSubmit = formValues) => {
    const isMailingAddressValid = validateAddressMatch(
      extractMailingAddress(valuesToSubmit),
      selectedSearchAddress,
    );
    const mailingAddressFields = buildAddressSubmissionFields(
      extractMailingAddress(valuesToSubmit),
      isMailingAddressValid,
      selectedSearchAddress,
    );

    const selectedAddressForMerchandise = merchandiseLocatedAtSameAddress
      ? selectedSearchAddress
      : selectedMerchandiseAddress;
    const isMerchandiseAddressValid = validateAddressMatch(
      extractMerchandiseAddress(valuesToSubmit),
      selectedAddressForMerchandise,
    );
    const merchandiseAddressFields = buildAddressSubmissionFields(
      extractMerchandiseAddress(valuesToSubmit),
      isMerchandiseAddressValid,
      selectedAddressForMerchandise,
    );

    store.send({
      type: StoreEvent.SUBMIT,
      data: {
        [StepKey.PERSONAL_DETAILS]: {
          [FormKey.FIRST_NAME]: valuesToSubmit.firstName,
          [FormKey.LAST_NAME]: valuesToSubmit.lastName,
          [FormKey.EMAIL]: valuesToSubmit.email,
          [FormKey.PHONE_NUMBER]: valuesToSubmit.phoneNumber,
          [FormKey.ADDRESS_LINE_1]: mailingAddressFields.addressLine1,
          [FormKey.SUB_BUILDING]: mailingAddressFields.subBuilding,
          [FormKey.STREET_NUMBER]: mailingAddressFields.streetNumber,
          [FormKey.STREET]: mailingAddressFields.street,
          [FormKey.PO_BOX]: mailingAddressFields.poBox,
          [FormKey.POSTAL_CODE]: mailingAddressFields.postalCode,
          [FormKey.STATE_CODE]: mailingAddressFields.regionOrStateCode,
          [FormKey.CITY]: mailingAddressFields.city,
          [FormKey.ADDRESS_ID]: mailingAddressFields.externalAVSAddressId,
          [FormKey.ADDRESS]: mailingAddressFields.address,
          [FormKey.IS_VALID]: isMailingAddressValid,
          [FormKey.MERCHANDISE_ADDRESS_LINE_1]: merchandiseAddressFields.addressLine1,
          [FormKey.MERCHANDISE_SUB_BUILDING]: merchandiseAddressFields.subBuilding,
          [FormKey.MERCHANDISE_STREET_NUMBER]: merchandiseAddressFields.streetNumber,
          [FormKey.MERCHANDISE_STREET]: merchandiseAddressFields.street,
          [FormKey.MERCHANDISE_PO_BOX]: merchandiseAddressFields.poBox,
          [FormKey.MERCHANDISE_CITY]: merchandiseAddressFields.city,
          [FormKey.MERCHANDISE_POSTAL_CODE]: merchandiseAddressFields.postalCode,
          [FormKey.MERCHANDISE_STATE_CODE]: merchandiseAddressFields.regionOrStateCode,
          [FormKey.MERCHANDISE_ADDRESS_ID]: merchandiseAddressFields.externalAVSAddressId,
          [FormKey.MERCHANDISE_ADDRESS]: merchandiseAddressFields.address,
          [FormKey.MERCHANDISE_IS_VALID]: isMerchandiseAddressValid,
        },
      },
    });
  };

  const onBlur = (e: FormChangeEvent, pageName: 'appliance-location' | 'personal-details') => {
    // Note: handleChange is intentionally NOT called here — it is already wired to onChange on
    // each input. Calling it here too triggers a  re-render and second onBlur fire,
    // which causes duplicate GA events in GTM/GA4.

    //Validate the input synchronously to ensure it is valid before firing GA event

    const validators = (
      formFieldValidators as Record<string, ((value: string) => string | undefined)[]>
    )[e.target.name];

    if (!validators) {
      return;
    }

    // Validate against the stored form value rather than e.target.value: masked inputs such as
    // PhoneInput display a formatted value (e.g. "(555) 123-4567") but store the normalised
    // value (e.g. "+15551234567") that the validators expect.
    const value = formValues[e.target.name as keyof typeof formValues] ?? e.target.value;

    const hasErrors = validators.some((validator) => {
      const error = validator(value);
      return error !== undefined && error !== '';
    });

    if (!hasErrors) {
      handleGaPersonalDetailsFormEvents(
        e.target.name,
        pageName,
        applianceCategory,
        applianceName,
        sessionId,
        systemCode,
        'Manual',
      );
    }
  };

  useEffect(() => {
    smoothScrollIntoView(progressBarRef.current);
  }, [displayMerchandiseLocationAddressForm]);

  if (quote?.status === QuoteStatus.LOADING) {
    return <LoadingPage />;
  }

  return (
    <PageBackgroundWrapper>
      <div className="grid grid-cols-1 gap-space-400">
        <ProgressBars ref={progressBarRef} total={totalSteps} current={stepNumber} />
        <h1 className="text-h3">{title}</h1>
        <p>This ensures your appliance is registered to the right person and location.</p>
        <form className={styles.formGrid}>
          {!merchandiseLocatedAtSameAddress && displayMerchandiseLocationAddressForm ? (
            <AddressForm
              formValues={formValues}
              errors={errors}
              onAddressSelect={handleAddressSelect}
              handleFormChange={handleChange}
              onBlur={(e) => onBlur(e, 'appliance-location')}
              addressType="merchandise"
              applianceCategory={applianceCategory}
              sessionId={sessionId}
              systemCode={systemCode}
            />
          ) : (
            <PersonalDetailsForm
              formValues={formValues}
              errors={errors}
              handleChange={handleChange}
              onBlur={(e) => onBlur(e, 'personal-details')}
              onAddressSelect={handleAddressSelect}
              setMerchandiseLocatedAtSameAddress={setMerchandiseLocatedAtSameAddress}
              merchandiseLocatedAtSameAddress={merchandiseLocatedAtSameAddress}
              isMailingAddressPoBox={isMailingAddressPoBox}
              applianceCategory={applianceCategory}
              sessionId={sessionId}
              systemCode={systemCode}
            />
          )}
        </form>
        {!displayMerchandiseLocationAddressForm && (
          <div className="flex flex-col mt-space-100 mb-space-100">
            <p>
              By continuing, you acknowledge and agree to the{' '}
              <a
                className="text-body-link-strong"
                href="https://www.domesticandgeneral.com/us/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>{' '}
              and acknowledge the privacy notice of{' '}
              <a
                className="text-body-link-strong"
                href="https://www.whirlpoolcorp.com/privacy-policy.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                {VITE_CLIENT_FRIENDLY_NAME}
              </a>{' '}
              and{' '}
              <a
                className="text-body-link-strong"
                href="https://www.domesticandgeneral.com/us/security-privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Domestic and General
              </a>
            </p>
          </div>
        )}
        <div className="flex flex-col w-full items-center gap-space-500 md:flex-row-reverse md:justify-between">
          <div className="w-full self-end md:w-fit">
            <Button onClick={handleContinue}>Continue</Button>
          </div>
          <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
            Go Back
          </TextLink>
        </div>
      </div>
    </PageBackgroundWrapper>
  );
}
